import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import {
  Mail, Upload, Globe, Send, Calendar as CalendarIcon, BarChart3,
  LogOut, Plus, Eye, Trash2, RefreshCw, Users, MousePointerClick,
  MailOpen, Clock, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet,
  ChevronRight, ChevronLeft, Sparkles, LayoutDashboard, History, Settings, Menu, X,
  Minus, PlusCircle, EyeOff, ChevronDown, ChevronUp, Image as ImageIcon, Palette, Share2, Copy, Download, Facebook, Instagram,
  HelpCircle, Maximize2, Minimize2, AlertTriangle, MessageSquare
} from "lucide-react";
import { ImagePicker } from "@/components/ImagePicker";
import { SectionImageManager } from "@/components/SectionImageManager";
import { GettingStartedPage, GettingStartedChecklist, TroubleshootingHelp } from "@/components/GettingStartedGuide";
import HelpPage from "@/HelpPage";
import SuitePage from "@/SuitePage";
import SimplySmarterEmailPage from "@/SimplySmarterEmailPage";
import TryPage from "@/TryPage";
import PricingPage from "@/PricingPage";
import BillingPage from "@/BillingPage";

// Extracted: auth context, layout chrome, simple public pages
import { AuthProvider, useAuth, ProtectedRoute, LoadingScreen, API } from "@/contexts/AuthContext";
import { Layout } from "@/components/layout/Layout";
import { PaywallModal } from "@/components/PaywallModal";
import ScrollToTop from "@/components/ScrollToTop";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import Dashboard from "@/pages/Dashboard";
import NewCampaign from "@/pages/NewCampaign";
import CampaignsList from "@/pages/CampaignsList";
import ContactsPage from "@/pages/ContactsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import OnboardingWizard from "@/pages/OnboardingWizard";
import SettingsPage from "@/pages/SettingsPage";
import CampaignDetail from "@/pages/CampaignDetail";

// Re-export useAuth so existing modules importing from "@/App" keep working
export { useAuth };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// API constant now lives in @/contexts/AuthContext (imported above)
// AuthContext, AuthProvider, ProtectedRoute, LoadingScreen also moved there
// LandingPage, LoginPage, Sidebar, Layout extracted to @/pages and @/components/layout

// Dashboard
// Dashboard extracted to @/pages/Dashboard.jsx
// NewCampaign extracted to @/pages/NewCampaign.jsx
// CampaignDetail extracted to @/pages/CampaignDetail.jsx
const HomeRoute = () => {
  const { loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return <SuitePage />;
};

// ScrollToTop and PaywallModal extracted to @/components/

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <PaywallModal />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/suite" element={<SuitePage />} />
          <Route path="/simply-smarter-email" element={<SimplySmarterEmailPage />} />
          <Route path="/try" element={<TryPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/billing" element={<ProtectedRoute><Layout><BillingPage /></Layout></ProtectedRoute>} />
          <Route path="/welcome" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/campaign/new" element={<ProtectedRoute><NewCampaign /></ProtectedRoute>} />
          <Route path="/campaign/:id" element={<ProtectedRoute><CampaignDetail /></ProtectedRoute>} />
          <Route path="/campaigns" element={<ProtectedRoute><CampaignsList /></ProtectedRoute>} />
          <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/getting-started" element={<ProtectedRoute><GettingStartedPage /></ProtectedRoute>} />
          <Route path="/help" element={<HelpPage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
